import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, LogOut, Calculator, ShoppingBag, Info, BookOpen } from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogin = () => {
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-50 border-b-2 border-zinc-950">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 flex items-center justify-between h-14">

        <Link to="/" className="flex items-center gap-3 group">
          {/* Brand mark */}
          <div className="w-7 h-7 bg-zinc-950 flex items-center justify-center border border-zinc-950 shrink-0 group-hover:bg-zinc-700 transition-colors duration-150">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="6" width="12" height="8" rx="0" stroke="white" strokeWidth="1.5"/>
              <path d="M4 6V4C4 2.343 5.343 1 7 1C8.657 1 10 2.343 10 4V6" stroke="white" strokeWidth="1.5" strokeLinecap="square"/>
              <circle cx="7" cy="10" r="1.2" fill="white"/>
            </svg>
          </div>
          {/* Wordmark */}
          <div className="flex flex-col leading-none">
            <span className="text-sm font-bold tracking-tight text-zinc-950 group-hover:text-zinc-600 transition-colors duration-150" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, letterSpacing: '-0.01em' }}>
              CalcByEA<span className="text-[0.6em] align-super" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>™</span>
            </span>
            <span className="hidden sm:block text-[8px] font-medium text-zinc-400 tracking-[0.2em] uppercase -mt-0.5">
              Premium Math
            </span>
          </div>
        </Link>

        <div className="flex items-center">
          <Link
            to="/calculator"
            className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 transition-all duration-100 text-xs tracking-widest uppercase px-2 sm:px-4 h-14 border-l border-zinc-200"
            data-testid="nav-calculator"
          >
            <Calculator size={13} strokeWidth={2.5} />
            <span className="hidden sm:inline">Calculator</span>
          </Link>

          <Link
            to="/store"
            className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 transition-all duration-100 text-xs tracking-widest uppercase px-2 sm:px-4 h-14 border-l border-zinc-200"
            data-testid="nav-store"
          >
            <ShoppingBag size={13} strokeWidth={2.5} />
            <span className="hidden sm:inline">DLC Store</span>
          </Link>

          <Link
            to="/about"
            className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 transition-all duration-100 text-xs tracking-widest uppercase px-2 sm:px-4 h-14 border-l border-zinc-200"
            data-testid="nav-about"
          >
            <Info size={13} strokeWidth={2.5} />
            <span className="hidden sm:inline">What is This?</span>
          </Link>

          <Link
            to="/blog"
            className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 transition-all duration-100 text-xs tracking-widest uppercase px-2 sm:px-4 h-14 border-l border-zinc-200"
            data-testid="nav-blog"
          >
            <BookOpen size={13} strokeWidth={2.5} />
            <span className="hidden sm:inline">Blog</span>
          </Link>

          {user?.is_admin && (
            <Link
              to="/admin"
              className="hidden sm:flex items-center gap-1.5 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 transition-all duration-100 text-xs tracking-widest uppercase px-2 sm:px-4 h-14 border-l border-zinc-200"
              data-testid="nav-admin"
            >
              <span>Admin</span>
            </Link>
          )}

          <div className="border-l border-zinc-200 h-14 flex items-center px-2 sm:px-3">
            {user ? (
              <div className="flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-6 h-6 border border-zinc-300 object-cover" />
                ) : (
                  <div className="w-6 h-6 bg-zinc-950 text-white flex items-center justify-center text-xs font-bold">
                    {user.name?.[0] || 'P'}
                  </div>
                )}
                <span className="hidden sm:inline text-xs text-zinc-500 uppercase tracking-wider">
                  {user.name?.split(' ')[0]}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-zinc-400 hover:text-zinc-950 transition-colors duration-100"
                  data-testid="logout-btn"
                >
                  <LogOut size={13} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="flex items-center gap-1.5 bg-zinc-950 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 border-2 border-zinc-950 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(9,9,11,1)] transition-all duration-100 whitespace-nowrap"
                data-testid="google-login-btn"
              >
                <LogIn size={12} strokeWidth={2.5} />
                Login
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
